import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import '@/index.css';

// Lazy-load pages
const HomePage = lazy(() => import('@/pages/public/HomePage'));
const AboutPage = lazy(() => import('@/pages/public/AboutPage'));
const FeaturesPage = lazy(() => import('@/pages/public/FeaturesPage'));

const DocsPage = lazy(() => import('@/pages/public/DocsPage'));
const ContactPage = lazy(() => import('@/pages/public/ContactPage'));
const DevelopersPage = lazy(() => import('@/pages/public/DevelopersPage'));
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage'));

// Merchant
const MerchantSetup = lazy(() => import('@/pages/merchant/MerchantSetupPage'));
const MerchantDashboard = lazy(() => import('@/pages/merchant/DashboardPage'));
const POSManagement = lazy(() => import('@/pages/merchant/POSManagementPage'));
const ProductsPage = lazy(() => import('@/pages/merchant/ProductsPage'));
const TransactionsPage = lazy(() => import('@/pages/merchant/TransactionsPage'));
const PayoutsPage = lazy(() => import('@/pages/merchant/PayoutsPage'));
const CardsPage = lazy(() => import('@/pages/merchant/CardsPage'));
const KYCPage = lazy(() => import('@/pages/merchant/KYCPage'));
const MerchantSettings = lazy(() => import('@/pages/merchant/SettingsPage'));

// POS
const POSLoginPage = lazy(() => import('@/pages/pos/POSLoginPage'));
const POSHomePage = lazy(() => import('@/pages/pos/POSHomePage'));
const POSPaymentPage = lazy(() => import('@/pages/pos/POSPaymentPage'));
const POSActivatePage = lazy(() => import('@/pages/pos/POSActivatePage'));
const POSHistoryPage = lazy(() => import('@/pages/pos/POSHistoryPage'));
const POSEndOfDayPage = lazy(() => import('@/pages/pos/POSEndOfDayPage'));
const POSCheckBalancePage = lazy(() => import('@/pages/pos/POSCheckBalancePage'));
const POSFundCardPage = lazy(() => import('@/pages/pos/POSFundCardPage'));
const POSTransactionReceiptPage = lazy(() => import('@/pages/pos/POSTransactionReceiptPage'));

// Admin
const AdminDashboard = lazy(() => import('@/pages/admin/AdminDashboardPage'));
const AdminMerchants = lazy(() => import('@/pages/admin/MerchantsPage'));
const AdminPayouts = lazy(() => import('@/pages/admin/PayoutRequestsPage'));
const AdminKYC = lazy(() => import('@/pages/admin/KYCManagementPage'));
const AdminHealth = lazy(() => import('@/pages/admin/HealthPage'));
const AdminTransactions = lazy(() => import('@/pages/admin/AdminTransactionsPage'));
const AdminProviderSettings = lazy(() => import('@/pages/admin/AdminProviderSettingsPage'));
const AdminMerchantDetail = lazy(() => import('@/pages/admin/AdminMerchantDetailPage'));
const AdminBusinessAnalytics = lazy(() => import('@/pages/admin/AdminBusinessAnalyticsPage'));
const AdminFraudAlerts = lazy(() => import('@/pages/admin/AdminFraudAlertsPage'));
const AdminSupport = lazy(() => import('@/pages/admin/AdminSupportPage'));
const AdminCharges = lazy(() => import('@/pages/admin/AdminChargesPage'));

// Merchant (new)
const MerchantFinancePage = lazy(() => import('@/pages/merchant/MerchantFinancePage'));
const MerchantChartOfAccountsPage = lazy(() => import('@/pages/merchant/MerchantChartOfAccountsPage'));
const MerchantProductAnalyticsPage = lazy(() => import('@/pages/merchant/MerchantProductAnalyticsPage'));
const MerchantCardDetailPage = lazy(() => import('@/pages/merchant/MerchantCardDetailPage'));
const MerchantPOSDetailPage = lazy(() => import('@/pages/merchant/MerchantPOSDetailPage'));
const MerchantSupportPage = lazy(() => import('@/pages/merchant/MerchantSupportPage'));
const DeveloperCenterPage = lazy(() => import('@/pages/merchant/DeveloperCenterPage'));

function ProtectedRoute({ children, requiredRoles }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (requiredRoles && !requiredRoles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function POSRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/pos/login" replace />;
  if (user.role !== 'POS_OPERATOR') return <Navigate to="/pos/login" replace />;
  return children;
}

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-[#f8faf7] flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-4 border-[#0a3d1f] border-t-transparent rounded-full animate-spin" />
        <p className="text-[#475549] font-inter text-sm">Loading Nairaxi...</p>
      </div>
    </div>
  );
}

function AppRoutes() {
  const { user, loading } = useAuth();

  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        {/* Public */}
        <Route path="/" element={<HomePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/features" element={<FeaturesPage />} />

        <Route path="/docs" element={<DocsPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/developers" element={<DevelopersPage />} />
        <Route path="/login" element={user && !loading ? <Navigate to={getDefaultRoute(user)} replace /> : <LoginPage />} />
        <Route path="/register" element={user && !loading ? <Navigate to="/merchant/setup" replace /> : <RegisterPage />} />

        {/* Merchant Portal */}
        <Route path="/merchant/setup" element={
          <ProtectedRoute requiredRoles={['MERCHANT_OWNER', 'SUPER_ADMIN']}>
            <MerchantSetup />
          </ProtectedRoute>
        } />
        <Route path="/merchant" element={
          <ProtectedRoute requiredRoles={['MERCHANT_OWNER', 'MERCHANT_ADMIN', 'MERCHANT_FINANCE']}>
            <MerchantDashboard />
          </ProtectedRoute>
        } />
        <Route path="/merchant/pos" element={
          <ProtectedRoute requiredRoles={['MERCHANT_OWNER', 'MERCHANT_ADMIN']}>
            <POSManagement />
          </ProtectedRoute>
        } />
        <Route path="/merchant/products" element={
          <ProtectedRoute requiredRoles={['MERCHANT_OWNER', 'MERCHANT_ADMIN']}>
            <ProductsPage />
          </ProtectedRoute>
        } />
        <Route path="/merchant/transactions" element={
          <ProtectedRoute requiredRoles={['MERCHANT_OWNER', 'MERCHANT_ADMIN', 'MERCHANT_FINANCE']}>
            <TransactionsPage />
          </ProtectedRoute>
        } />
        <Route path="/merchant/payouts" element={
          <ProtectedRoute requiredRoles={['MERCHANT_OWNER', 'MERCHANT_FINANCE']}>
            <PayoutsPage />
          </ProtectedRoute>
        } />
        <Route path="/merchant/cards" element={
          <ProtectedRoute requiredRoles={['MERCHANT_OWNER', 'MERCHANT_ADMIN']}>
            <CardsPage />
          </ProtectedRoute>
        } />
        <Route path="/merchant/kyc" element={
          <ProtectedRoute requiredRoles={['MERCHANT_OWNER']}>
            <KYCPage />
          </ProtectedRoute>
        } />
        <Route path="/merchant/settings" element={
          <ProtectedRoute requiredRoles={['MERCHANT_OWNER']}>
            <MerchantSettings />
          </ProtectedRoute>
        } />

        {/* POS Terminal */}
        <Route path="/pos/login" element={<POSLoginPage />} />
        <Route path="/pos" element={<POSRoute><POSHomePage /></POSRoute>} />
        <Route path="/pos/payment" element={<POSRoute><POSPaymentPage /></POSRoute>} />
        <Route path="/pos/activate" element={<POSRoute><POSActivatePage /></POSRoute>} />
        <Route path="/pos/history" element={<POSRoute><POSHistoryPage /></POSRoute>} />
        <Route path="/pos/end-of-day" element={<POSRoute><POSEndOfDayPage /></POSRoute>} />
        <Route path="/pos/balance" element={<POSRoute><POSCheckBalancePage /></POSRoute>} />
        <Route path="/pos/fund-card" element={<POSRoute><POSFundCardPage /></POSRoute>} />
        <Route path="/pos/receipt/:ref" element={<POSRoute><POSTransactionReceiptPage /></POSRoute>} />

        {/* Master Admin */}
        <Route path="/admin" element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ADMIN', 'FINANCE_ADMIN', 'KYC_ADMIN', 'SUPPORT_ADMIN']}>
            <AdminDashboard />
          </ProtectedRoute>
        } />
        <Route path="/admin/merchants" element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ADMIN', 'KYC_ADMIN', 'SUPPORT_ADMIN']}>
            <AdminMerchants />
          </ProtectedRoute>
        } />
        <Route path="/admin/payouts" element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ADMIN', 'FINANCE_ADMIN']}>
            <AdminPayouts />
          </ProtectedRoute>
        } />
        <Route path="/admin/kyc" element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ADMIN', 'KYC_ADMIN']}>
            <AdminKYC />
          </ProtectedRoute>
        } />
        <Route path="/admin/health" element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ADMIN']}>
            <AdminHealth />
          </ProtectedRoute>
        } />
        <Route path="/admin/transactions" element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ADMIN', 'FINANCE_ADMIN']}>
            <AdminTransactions />
          </ProtectedRoute>
        } />
        <Route path="/admin/provider-settings" element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN']}>
            <AdminProviderSettings />
          </ProtectedRoute>
        } />
        <Route path="/admin/merchants/:id" element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ADMIN', 'FINANCE_ADMIN', 'KYC_ADMIN', 'SUPPORT_ADMIN']}>
            <AdminMerchantDetail />
          </ProtectedRoute>
        } />
        <Route path="/admin/analytics" element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ADMIN', 'FINANCE_ADMIN']}>
            <AdminBusinessAnalytics />
          </ProtectedRoute>
        } />
        <Route path="/admin/fraud" element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ADMIN', 'KYC_ADMIN', 'SUPPORT_ADMIN']}>
            <AdminFraudAlerts />
          </ProtectedRoute>
        } />
        <Route path="/admin/support" element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ADMIN', 'SUPPORT_ADMIN']}>
            <AdminSupport />
          </ProtectedRoute>
        } />

        <Route path="/admin/charges" element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ADMIN', 'FINANCE_ADMIN']}>
            <AdminCharges />
          </ProtectedRoute>
        } />

        {/* Merchant Portal (new pages) */}
        <Route path="/merchant/finance" element={
          <ProtectedRoute requiredRoles={['MERCHANT_OWNER', 'MERCHANT_FINANCE']}>
            <MerchantFinancePage />
          </ProtectedRoute>
        } />
        <Route path="/merchant/chart-of-accounts" element={
          <ProtectedRoute requiredRoles={['MERCHANT_OWNER', 'MERCHANT_FINANCE']}>
            <MerchantChartOfAccountsPage />
          </ProtectedRoute>
        } />
        <Route path="/merchant/analytics" element={
          <ProtectedRoute requiredRoles={['MERCHANT_OWNER', 'MERCHANT_ADMIN']}>
            <MerchantProductAnalyticsPage />
          </ProtectedRoute>
        } />
        <Route path="/merchant/cards/:id" element={
          <ProtectedRoute requiredRoles={['MERCHANT_OWNER', 'MERCHANT_ADMIN']}>
            <MerchantCardDetailPage />
          </ProtectedRoute>
        } />
        <Route path="/merchant/pos/:id" element={
          <ProtectedRoute requiredRoles={['MERCHANT_OWNER', 'MERCHANT_ADMIN']}>
            <MerchantPOSDetailPage />
          </ProtectedRoute>
        } />
        <Route path="/merchant/support" element={
          <ProtectedRoute requiredRoles={['MERCHANT_OWNER', 'MERCHANT_ADMIN', 'MERCHANT_FINANCE']}>
            <MerchantSupportPage />
          </ProtectedRoute>
        } />
        <Route path="/merchant/developer" element={
          <ProtectedRoute requiredRoles={['MERCHANT_OWNER', 'MERCHANT_ADMIN']}>
            <DeveloperCenterPage />
          </ProtectedRoute>
        } />

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function getDefaultRoute(user) {
  if (!user) return '/login';
  if (user.role === 'POS_OPERATOR') return '/pos';
  if (['SUPER_ADMIN', 'ADMIN', 'FINANCE_ADMIN', 'KYC_ADMIN', 'SUPPORT_ADMIN'].includes(user.role)) return '/admin';
  if (user.merchant_id) return '/merchant';
  return '/merchant/setup';
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors closeButton />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
